import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded'
import { Box, Tooltip, Typography } from '@mui/material'

import { clickableProps } from '../../helpers/interactive'
import { focusRingSx } from '../../helpers/styles'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { TagRef } from '../../types'
import { EmptyHint, ViewAllButton } from '../OverviewPrimitives/OverviewPrimitives'
import { SectionCard } from '../SectionCard/SectionCard'

/** Number of items the tags section previews before "View all" takes over. */
const TAG_PREVIEW = 6

/** A creative tag both sides use, with per-side counts and share-of-creatives. */
export interface SharedTag {
  tag: TagRef
  sourceCount: number
  targetCount: number
  /** % of the source's creatives that use the tag (0–100). */
  sourceShare: number
  /** % of the target's creatives that use the tag (0–100). */
  targetShare: number
}

interface CompareSharedTagsSectionProps {
  sharedTags: SharedTag[]
  sourceName: string
  targetName: string
  /** Source game icon URL; empty falls back to a generic badge. */
  sourceIcon?: string
  /** Target game/genre icon URL; empty (genres) falls back to a generic badge. */
  targetIcon?: string
  sourceTotalCreatives: number
  targetTotalCreatives: number
  /** Open the full Creative Tags tab. */
  onViewAll: () => void
  /** Open the Creative Tags tab scrolled to a specific tag. */
  onOpenTag: (tagId: string) => void
}

/**
 * Overview-tab "Creative Tags" section: the tactics both sides lean on,
 * previewed as clickable back-to-back share bars with a colour legend, routing
 * into the full Creative Tags tab. Extracted from `CompareOverviewTab`; the
 * parent owns the shared-tag derivation, this component only renders.
 */
export const CompareSharedTagsSection: FC<CompareSharedTagsSectionProps> = ({
  sharedTags,
  sourceName,
  targetName,
  sourceIcon,
  targetIcon,
  sourceTotalCreatives,
  targetTotalCreatives,
  onViewAll,
  onOpenTag,
}) => {
  const { t } = useTranslation('ua-insights')
  const { tagLabel, dimensionLabel } = useUATaxonomy()

  return (
    <SectionCard
      title={t('compare_overview_creative_tags_title')}
      headerAction={<ViewAllButton onClick={onViewAll} label={t('overview_open_section', { section: t('compare_tab_creative_tags') })} />}
    >
      {sharedTags.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {/* Legend — maps each twin-bar colour to its side so the counts are
              self-explanatory without a deep dive into the full tab. */}
          <SharedTagLegend
            caption={t('compare_tag_legend_caption')}
            sourceName={sourceName}
            targetName={targetName}
            sourceIcon={sourceIcon}
            targetIcon={targetIcon}
          />
          <SharedTagHeader tagColumnLabel={t('compare_tag_header_tag')} countLabel={t('compare_tag_header_count')} />
          {sharedTags.slice(0, TAG_PREVIEW).map((tag) => (
            <SharedTagRow
              key={tag.tag.id}
              label={tagLabel(tag.tag)}
              caption={dimensionLabel(tag.tag.dimensionId)}
              sourceShare={tag.sourceShare}
              targetShare={tag.targetShare}
              sourceName={sourceName}
              targetName={targetName}
              shareTooltip={t('compare_tag_share_tooltip', {
                source: sourceName,
                sourceCount: tag.sourceCount,
                sourceTotal: sourceTotalCreatives,
                sourceShare: Math.round(tag.sourceShare),
                target: targetName,
                targetCount: tag.targetCount,
                targetTotal: targetTotalCreatives,
                targetShare: Math.round(tag.targetShare),
              })}
              onClick={() => onOpenTag(tag.tag.id)}
            />
          ))}
        </Box>
      ) : (
        <EmptyHint text={t('compare_tag_shared_empty')} />
      )}
    </SectionCard>
  )
}

interface SharedTagLegendProps {
  /** Short line clarifying what the twin bars / trailing counts represent. */
  caption: string
  sourceName: string
  targetName: string
  sourceIcon?: string
  targetIcon?: string
}

/**
 * Legend for the Creative Tags twin bars: a one-line explainer plus a colour
 * key tying the secondary-coloured (source) and primary-coloured (target) bars
 * to their game/genre. Reuses the dumbbell's ringed-icon vocabulary so the
 * Overview reads as one consistent system.
 */
const SharedTagLegend: FC<SharedTagLegendProps> = ({ caption, sourceName, targetName, sourceIcon, targetIcon }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pb: 1 }}>
    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.4 }}>{caption}</Typography>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 1.5, sm: 2.5 } }}>
      <LegendEntry color="secondary.main" name={sourceName} icon={sourceIcon} />
      <LegendEntry color="primary.main" name={targetName} icon={targetIcon} />
    </Box>
  </Box>
)

/** Single colour-key entry: a side-coloured ringed icon + the entity name. */
const LegendEntry: FC<{ color: string; name: string; icon?: string }> = ({ color, name, icon }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
    <Box
      sx={{
        flexShrink: 0,
        width: 16,
        height: 16,
        borderRadius: '28%',
        border: '2px solid',
        borderColor: color,
        bgcolor: 'background.paper',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {icon ? (
        <Box component="img" src={icon} alt={name} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      ) : (
        <CategoryRoundedIcon sx={{ fontSize: 10, color }} />
      )}
    </Box>
    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</Typography>
  </Box>
)

/**
 * Column headers for the shared-tag list, laid out on the same grid as
 * `SharedTagRow` so the "Tag" label and the trailing "Creatives" count line up
 * with the rows below.
 */
const SharedTagHeader: FC<{ tagColumnLabel: string; countLabel: string }> = ({ tagColumnLabel, countLabel }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1, mx: -1, pb: 0.25 }}>
    <Typography
      sx={{
        width: { xs: 120, sm: 170 },
        flexShrink: 0,
        fontSize: '0.6875rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        color: 'text.secondary',
      }}
    >
      {tagColumnLabel}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 40, display: 'flex', justifyContent: 'center' }}>
      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
        {countLabel}
      </Typography>
    </Box>
  </Box>
)

interface SharedTagRowProps {
  label: string
  caption?: string
  /** Source side: % of its creatives that use the tag (0–100). */
  sourceShare: number
  /** Target side: % of its creatives that use the tag (0–100). */
  targetShare: number
  sourceName: string
  targetName: string
  /** Pre-built "uses it in N of M creatives" explainer for both sides. */
  shareTooltip: string
  onClick: () => void
}

/**
 * Clickable shared-tag row built around a single back-to-back (diverging) bar:
 * the source side grows leftward from a centre axis, the target side rightward,
 * each scaled to its own share-of-creatives (0–100%). Replacing the old twin
 * full-width count bars, this keeps one slim row per tag, normalises away the
 * two sides' very different creative volumes, and makes the lean (who leans on
 * the tag harder) readable at a glance. Each side's share is labelled at the
 * bar's outer end; the raw "N of M creatives" lives in the row tooltip.
 */
const SharedTagRow: FC<SharedTagRowProps> = ({ label, caption, sourceShare, targetShare, sourceName, targetName, shareTooltip, onClick }) => (
  <Tooltip title={shareTooltip} arrow placement="top">
    <Box
      {...clickableProps(onClick)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1,
        py: 0.625,
        mx: -1,
        borderRadius: 1,
        cursor: 'pointer',
        transition: 'background-color 0.1s ease',
        '&:hover': { bgcolor: 'action.hover' },
        ...focusRingSx(-2),
      }}
    >
      <Box sx={{ width: { xs: 120, sm: 170 }, flexShrink: 0, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Typography>
        {caption && (
          <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {caption}
          </Typography>
        )}
      </Box>
      <DivergingShareBar sourceShare={sourceShare} targetShare={targetShare} sourceName={sourceName} targetName={targetName} />
    </Box>
  </Tooltip>
)

interface DivergingShareBarProps {
  sourceShare: number
  targetShare: number
  sourceName: string
  targetName: string
}

/** Width (px) of the share-% label sitting at each outer end of the bar. */
const SHARE_LABEL_WIDTH = 34

/**
 * Back-to-back share bar: a centre axis with the source bar filling leftward
 * (secondary colour) and the target bar filling rightward (primary colour),
 * each on its own 0–100% scale so the two sides are directly comparable
 * regardless of how many creatives each ran. The outer share-% labels are
 * colour-keyed to their side so the bar reads without leaning on the legend.
 */
const DivergingShareBar: FC<DivergingShareBarProps> = ({ sourceShare, targetShare, sourceName, targetName }) => {
  const { t } = useTranslation('ua-insights')

  return (
    <Box sx={{ flex: 1, minWidth: 80, display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography
        aria-label={t('compare_tag_share_label', { name: sourceName, share: Math.round(sourceShare) })}
        sx={{
          width: SHARE_LABEL_WIDTH,
          flexShrink: 0,
          textAlign: 'right',
          fontSize: '0.6875rem',
          fontWeight: 700,
          color: 'secondary.main',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {`${Math.round(sourceShare)}%`}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 32, display: 'flex', alignItems: 'center', height: 10 }}>
        {/* Source half — track fills from the centre outward to the left. */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            height: 8,
            bgcolor: 'action.hover',
            borderRadius: '999px 0 0 999px',
            display: 'flex',
            justifyContent: 'flex-end',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ width: `${sourceShare}%`, height: '100%', bgcolor: 'secondary.main', borderRadius: '999px 0 0 999px', transition: 'width 0.3s ease' }} />
        </Box>
        {/* Centre axis the two sides diverge from. */}
        <Box sx={{ width: '2px', height: 12, bgcolor: 'divider', flexShrink: 0, borderRadius: '999px' }} />
        {/* Target half — track fills from the centre outward to the right. */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            height: 8,
            bgcolor: 'action.hover',
            borderRadius: '0 999px 999px 0',
            display: 'flex',
            justifyContent: 'flex-start',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ width: `${targetShare}%`, height: '100%', bgcolor: 'primary.main', borderRadius: '0 999px 999px 0', transition: 'width 0.3s ease' }} />
        </Box>
      </Box>
      <Typography
        aria-label={t('compare_tag_share_label', { name: targetName, share: Math.round(targetShare) })}
        sx={{
          width: SHARE_LABEL_WIDTH,
          flexShrink: 0,
          textAlign: 'left',
          fontSize: '0.6875rem',
          fontWeight: 700,
          color: 'primary.main',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {`${Math.round(targetShare)}%`}
      </Typography>
    </Box>
  )
}
